import { useMutation, useQuery } from "@apollo/client";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ZoomIn from "@mui/icons-material/ZoomIn";
import ZoomOut from "@mui/icons-material/ZoomOut";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Grid,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import {
  DotMarkingContainer,
  dotMarkingGroupFor,
  requiresHazardLabel,
  requiresLargeIdNumber,
} from "encamp-shared/src/waste/dotMarkingGroup";
import {
  hazardClassNeedsDivision,
  regulatedLabelProblem,
} from "encamp-shared/src/waste/hazardClassDiamond";
import { hasDotBlock } from "encamp-shared/src/waste/hazardousWasteLabelVisuals";
import { pngExportLoss } from "encamp-shared/src/waste/pngExportLoss";
import { GetContainerLabelQuery, LabelExportFormat, LabelFormat } from "generated-graphql/graphql";
import { ReactElement, useRef, useState } from "react";
import { downloadFromUrl } from "util/download";

import {
  EXPORT_CONTAINER_LABELS_MUTATION,
  GET_CONTAINER_LABEL_QUERY,
  SET_CONTAINER_LABEL_NOTE_MUTATION,
} from "../api";
import { FormatRadioGroup } from "./LabelFormatControls";
import {
  LabelSizeOption,
  PAGE_SIZE_BY_LABEL_SIZE,
  PREVIEW_SIZE_BY_LABEL_SIZE,
} from "./labelFormState";
import {
  isClassificationMissing,
  isAccumulationStartDateMissing,
  isHazardIndicationMissing,
  isUniversalWasteTypeMissing,
} from "./labelPreviewContent";
import { LabelPreviewStage } from "./LabelPreviewStage";
import { Eyebrow } from "./LabelScreenTypography";

type ContainerLabelData = NonNullable<GetContainerLabelQuery["wasteContainer"]>;

/** A fixed stage so the dialog holds still: the label's own size changes with
 * the format (a QR-only label is a fraction of a full one) and with the
 * reader's zoom, and none of that should move the controls beside it. */
const PREVIEW_STAGE_HEIGHT = 420;

/** Zoom steps to two decimals, so repeated +/- can't drift onto values like
 * 1.7500000000000002 and render a nonsense percentage. */
function roundZoom(value: number): number {
  return Math.round(value * 100) / 100;
}

const ZOOM_STEP = 0.25;
const ZOOM_BOUNDS = { min: 0.5, max: 2 };

/** A package of liquid needs the 49 CFR 172.312 orientation arrows, and the
 * label cannot print them without knowing the waste is a liquid. */
function missingPhysicalStateForArrows(
  label: ContainerLabelData["label"],
  container: DotMarkingContainer
): boolean {
  const group = dotMarkingGroupFor(container);
  return (
    group !== undefined &&
    requiresHazardLabel(group) &&
    hasDotBlock(label) &&
    label.physicalState == null
  );
}

/** Class 7 draws its trefoil on the label but takes no regulated full-size
 * label, because the category depends on readings a waste profile has not
 * got. See regulatedLabelProblem. */
function hasUnprintableRegulatedLabel(
  label: ContainerLabelData["label"],
  container: DotMarkingContainer
): boolean {
  const group = dotMarkingGroupFor(container);
  if (group === undefined || !requiresHazardLabel(group)) {
    return false;
  }
  return [label.dotHazardClass, label.dotSubsidiaryHazardClass].some(
    (hazardClass) => regulatedLabelProblem(hazardClass) !== undefined
  );
}

/** "2" and "6" name a class with no division. The symbol differs by division,
 * so the design prints with a blank upper half. Names whichever class is at
 * fault, primary first. */
function divisionUnknownWarning(label: ContainerLabelData["label"]): string | undefined {
  const [prefix, hazardClass] = hazardClassNeedsDivision(label.dotHazardClass)
    ? ["Hazard class", label.dotHazardClass]
    : ["Subsidiary hazard class", label.dotSubsidiaryHazardClass];
  if (!hazardClassNeedsDivision(hazardClass)) {
    return undefined;
  }
  return `${prefix} ${hazardClass?.trim()} has no division, so the label prints no hazard symbol. Set the division (2.1, 2.2, 2.3 / 6.1, 6.2) on the waste stream.`;
}

/** A bulk container or portable tank's whole DOT marking IS the ID number, so
 * without one it gets no marking page at all. */
function missingIdNumberForBulkMarking(
  label: ContainerLabelData["label"],
  container: DotMarkingContainer
): boolean {
  const group = dotMarkingGroupFor(container);
  return group !== undefined && requiresLargeIdNumber(group) && !label.dotIdNumber;
}

/**
 * The compliance warnings that sit above the preview: what this label will
 * print that it should not, or will omit that it should carry. Extracted so
 * PreviewCard stays about the preview.
 */
function LabelComplianceWarnings({
  label,
  container,
}: {
  label: ContainerLabelData["label"];
  container: DotMarkingContainer;
}): ReactElement {
  const divisionWarning = divisionUnknownWarning(label);
  return (
    <>
      {/* Not a data-entry nicety: an indication of the hazards of the contents
          is one of the three marks 40 CFR 262.17(a)(5)(i)(B) requires, so a
          label carrying none is missing a required mark. Only fires when
          neither the DOT diamond nor the waste codes can supply one. */}
      {isHazardIndicationMissing(label) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No hazard information — the label will print without an indication of the hazards of its
          contents, which 40 CFR 262.17(a)(5)(i)(B) requires alongside the marking. Set the waste
          stream&rsquo;s DOT profile or its EPA waste codes so the label can state the hazard.
        </Alert>
      )}

      {isAccumulationStartDateMissing(label) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No accumulation start date — this container sits in a central accumulation area, where 40
          CFR 262.17(a)(5)(i)(C) requires the date to be visible on the container. Set it before
          printing.
        </Alert>
      )}

      {/* Not cosmetic: 40 CFR 273.14 requires the marking to name the type of
          universal waste, so a label without it is not a compliant marking.
          Warning is the honest surface until the type reaches the label --
          printing it silently would look correct and not be. */}
      {isUniversalWasteTypeMissing(label) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Universal waste type not set — label will print &ldquo;UNIVERSAL WASTE&rdquo; without
          naming the type (e.g. &ldquo;Lamps&rdquo;), which 40 CFR 273.14 requires. Write the type
          on the label by hand before use.
        </Alert>
      )}

      {/* The marking has to say something, and with no classification to read
          the safe thing to say is the hazardous one — over-marking costs
          stricter handling, under-marking a hazardous drum is the violation.
          It is a default rather than a finding about this container, so it is
          surfaced instead of printed quietly. */}
      {isClassificationMissing(label) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No waste stream assigned — label will print the conservative &ldquo;HAZARDOUS WASTE&rdquo;
          marking. Assign this container&rsquo;s waste stream so the label states what it actually
          holds.
        </Alert>
      )}

      {missingPhysicalStateForArrows(label, container) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This container&rsquo;s waste stream has no physical state, so the label prints no
          orientation arrows. 49 CFR 172.312 requires them on a package of liquid. Set the physical
          state on the waste profile.
        </Alert>
      )}

      {hasUnprintableRegulatedLabel(label, container) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {regulatedLabelProblem(label.dotHazardClass) ??
            regulatedLabelProblem(label.dotSubsidiaryHazardClass)}
        </Alert>
      )}

      {divisionWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {divisionWarning}
        </Alert>
      )}

      {missingIdNumberForBulkMarking(label, container) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This container needs its ID number displayed at 100 mm on each side and each end. The
          waste stream has no DOT ID number, so that page is not printed.
        </Alert>
      )}
    </>
  );
}

function PreviewCard({
  container,
  layout,
  branded,
  note,
  size,
}: {
  container: ContainerLabelData;
  layout: LabelFormat;
  branded: boolean;
  note: string;
  size: LabelSizeOption;
}): ReactElement {
  const [zoomFactor, setZoomFactor] = useState(1);
  const stepZoom = (delta: number) =>
    setZoomFactor((current) =>
      Math.min(ZOOM_BOUNDS.max, Math.max(ZOOM_BOUNDS.min, roundZoom(current + delta)))
    );
  return (
    <Box>
      <LabelComplianceWarnings
        label={container.label}
        // physicalState lives on the label content, capacity on the
        // container, and the bulk threshold reads both.
        container={{
          containerType: container.containerType,
          capacity: container.capacity,
          capacityUnit: container.capacityUnit,
          physicalState: container.label.physicalState,
        }}
      />

      {/* The artifact is what the reader opened this to check, so it leads and
          gets the full width; the choices that change it sit underneath. */}
      <Stack alignItems="stretch">
        <Box sx={{ width: "100%" }}>
          <LabelPreviewStage
            // Tighter than the stage's roomy full-page default: in a dialog
            // that padding is space the controls need.
            stagePadding={2}
            content={container.label}
            layout={layout}
            branded={branded}
            noteOverride={note}
            size={PREVIEW_SIZE_BY_LABEL_SIZE[size]}
            containerType={container.containerType}
            capacity={container.capacity}
            capacityUnit={container.capacityUnit}
            zoomFactor={zoomFactor}
            // Fixed so the dialog doesn't resize under the reader when the
            // label's own dimensions change -- a QR-only label is a fraction
            // of a full one's height.
            stageHeight={PREVIEW_STAGE_HEIGHT}
          />
        </Box>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mt: 1 }}
        >
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                aria-label="Zoom out"
                disabled={zoomFactor <= ZOOM_BOUNDS.min}
                onClick={() => stepZoom(-ZOOM_STEP)}
              >
                <ZoomOut fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 44, textAlign: "center" }}
          >
            {Math.round(zoomFactor * 100)}%
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                aria-label="Zoom in"
                disabled={zoomFactor >= ZOOM_BOUNDS.max}
                onClick={() => stepZoom(ZOOM_STEP)}
              >
                <ZoomIn fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
}

/** The Print & format card's mutation plumbing (save note / export),
 * split out of `ContainerLabelBody` so that component stays a plain layout. */
function useLabelActions(
  container: ContainerLabelData,
  {
    note,
    layout,
    branded,
    size,
  }: {
    note: string;
    layout: LabelFormat;
    branded: boolean;
    size: LabelSizeOption;
  }
) {
  const alerts = useAlerts();
  const [saveNote] = useMutation(SET_CONTAINER_LABEL_NOTE_MUTATION);
  const [exportLabels, { loading: exporting }] = useMutation(EXPORT_CONTAINER_LABELS_MUTATION);
  // Bridges the gap between clicking Export and `exportLabels` actually
  // firing below -- see `handleExport`. Without it, the Export button's
  // `loading` prop (driven by `exporting`, the export mutation's own
  // `loading`) would stay false while a dirty note save is in flight,
  // reading as if the click did nothing.
  const [savingNoteBeforeExport, setSavingNoteBeforeExport] = useState(false);

  // The no-op guard can't compare against `container.labelNote` -- that prop
  // stays stale until the save's response lands in the cache, so a
  // blur-refocus-blur before it resolves would see the same "unsaved" note
  // both times and fire an identical second mutation. Tracking the
  // last-submitted value in a ref (updated synchronously, before the
  // mutation call) closes that window; reverting it on error lets a retry
  // fire again.
  const lastSubmittedNoteRef = useRef(container.labelNote ?? "");
  // The save triggered by the note field's own blur -- when Export is
  // clicked with the note field focused, that blur fires (and this ref gets
  // set) synchronously before Export's own click handler runs, so
  // `commitNoteIfDirty` below sees the note as already "submitted" and
  // needs this to still await the save that's actually in flight.
  const pendingNoteSaveRef = useRef<Promise<unknown> | null>(null);

  /** Saves the note if it's changed since the last submit, returning a
   * promise that resolves once that save (new or already in flight) settles.
   * Resolves immediately when there's nothing to save. */
  const commitNoteIfDirty = (): Promise<unknown> => {
    if (note === lastSubmittedNoteRef.current) {
      return pendingNoteSaveRef.current ?? Promise.resolve();
    }
    const previouslySubmitted = lastSubmittedNoteRef.current;
    lastSubmittedNoteRef.current = note;
    const savePromise = saveNote({
      variables: {
        containerId: container.id,
        note: note.length > 0 ? note : null,
      },
      onError: (mutationError) => {
        lastSubmittedNoteRef.current = previouslySubmitted;
        alerts.error(mutationError.message || "Failed to save the label note.");
      },
    });
    pendingNoteSaveRef.current = savePromise;
    return savePromise;
  };

  const handleNoteBlur = () => {
    void commitNoteIfDirty();
  };

  const runExport = (fileType: LabelExportFormat, viewer?: Window | null) => {
    exportLabels({
      variables: {
        containerIds: [container.id],
        layout,
        fileType,
        branded,
        pageSize: PAGE_SIZE_BY_LABEL_SIZE[size],
        inline: viewer != null,
      },
      onCompleted: (result) => {
        const { url } = result.exportContainerLabels;
        if (viewer) {
          viewer.location.href = url;
          return;
        }
        // The download's filename (container-label-<label>-<timestamp>) is
        // signed into the URL server-side — a name passed to the anchor here
        // would be ignored cross-origin.
        downloadFromUrl(url);
      },
      onError: (mutationError) => {
        viewer?.close();
        alerts.error(mutationError.message || "Failed to export the label.");
      },
    });
  };

  // Clicking Export blurs the note field first (moving focus to the
  // button), which independently fires a save -- racing it against the
  // export would let the exported PDF miss a note the preview already
  // shows. Awaiting the (possibly already in-flight) save first guarantees
  // the export always reflects the note as of the click, whether it failed
  // or not -- a failed save already surfaces its own alert above, and
  // blocking the export on top of that wouldn't help the user recover.
  const handleExport = (fileType: LabelExportFormat) => {
    setSavingNoteBeforeExport(true);
    void commitNoteIfDirty()
      .catch(() => undefined)
      .finally(() => {
        setSavingNoteBeforeExport(false);
        runExport(fileType);
      });
  };

  /**
   * Printing goes through the same generated document as downloading, opened
   * in the browser's PDF viewer rather than saved. The viewer is what prints
   * every page -- the DOM preview this dialog shows is one page, and a
   * container that takes a regulated hazard label or the 100mm ID number
   * needs more than one.
   *
   * The tab is opened on the click itself, before the mutation, and pointed
   * at the URL once it resolves. Opening it afterwards lands a tick too late
   * to count as a user gesture and Safari's popup blocker eats it -- the same
   * reason `downloadFromUrl` uses an anchor rather than `window.open`.
   */
  const handlePrint = () => {
    const viewer = window.open("", "_blank");
    setSavingNoteBeforeExport(true);
    void commitNoteIfDirty()
      .catch(() => undefined)
      .finally(() => {
        setSavingNoteBeforeExport(false);
        runExport(LabelExportFormat.Pdf, viewer);
      });
  };

  return {
    exporting: exporting || savingNoteBeforeExport,
    handleNoteBlur,
    handleExport,
    handlePrint,
  };
}

/**
 * One action with a choice of flavor, as a split button: the main half runs
 * what it says, the caret changes which. Two plain buttons per action read as
 * four unrelated things when they are really two, each with a format to pick.
 */
function SplitActionButton<T extends string>({
  options,
  value,
  onChange,
  onRun,
  variant,
  loading,
  chooseLabel,
}: {
  options: { value: T; label: string; disabled?: boolean; disabledReason?: string }[];
  value: T;
  onChange: (next: T) => void;
  onRun: (next: T) => void;
  variant: "outlined" | "contained";
  loading?: boolean;
  chooseLabel: string;
}): ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selected = options.find((option) => option.value === value) ?? options[0];

  return (
    <>
      <ButtonGroup variant={variant}>
        <Button onClick={() => onRun(selected.value)} loading={loading}>
          {selected.label}
        </Button>
        <Button
          size="small"
          aria-label={chooseLabel}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <ArrowDropDown fontSize="small" />
        </Button>
      </ButtonGroup>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            disabled={option.disabled}
            onClick={() => {
              onChange(option.value);
              setAnchorEl(null);
            }}
          >
            <ListItemText
              primary={option.label}
              secondary={option.disabled ? option.disabledReason : undefined}
              slotProps={{ secondary: { sx: { whiteSpace: "normal", maxWidth: 280 } } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** 4 x 6 label stock or a letter page. The chosen size is what the preview
 * draws and what the downloaded document is rendered at, so the two cannot
 * disagree. */
function PageSizeToggle({
  value,
  onChange,
}: {
  value: LabelSizeOption;
  onChange: (next: LabelSizeOption) => void;
}): ReactElement {
  return (
    <ToggleButtonGroup
      color="primary"
      exclusive
      size="small"
      value={value}
      aria-label="Page size"
      onChange={(_event, next: LabelSizeOption | null) => {
        if (next !== null) {
          onChange(next);
        }
      }}
      sx={{ mt: 1 }}
    >
      <ToggleButton value="4x6">4 &times; 6 in</ToggleButton>
      <ToggleButton value="letter">Letter</ToggleButton>
    </ToggleButtonGroup>
  );
}

/** Why Download PNG cannot be offered, or undefined when it can. The export
 * lambda refuses the same cases, FULL layout only, through the same shared
 * `pngExportLoss`. */
function pngDisabledReasonFor(
  container: ContainerLabelData,
  layout: LabelFormat
): string | undefined {
  if (layout !== LabelFormat.Full) {
    return undefined;
  }
  const loss = pngExportLoss(
    {
      containerType: container.containerType,
      capacity: container.capacity,
      capacityUnit: container.capacityUnit,
      physicalState: container.label.physicalState,
    },
    container.label
  );
  return loss
    ? `PNG can't carry the ${loss}, which prints on a second page. Download the PDF.`
    : undefined;
}

/** The dialog's two actions, built outside the component so its body stays a
 * layout rather than a layout plus a footer. */
function labelDialogActions({
  fileType,
  setFileType,
  handleExport,
  handlePrint,
  exporting,
  pngDisabledReason,
}: {
  pngDisabledReason?: string;
  fileType: LabelExportFormat;
  setFileType: (next: LabelExportFormat) => void;
  handleExport: (next: LabelExportFormat) => void;
  handlePrint: () => void;
  exporting: boolean;
}): ReactElement[] {
  // Both actions render the same generated document; they differ in where it
  // lands. Neither carries a size any more -- the page size is chosen beside
  // the preview that shows it, so the two cannot disagree.
  return [
    <SplitActionButton
      key="download"
      variant="outlined"
      chooseLabel="Choose download format"
      value={fileType}
      onChange={setFileType}
      onRun={handleExport}
      loading={exporting}
      options={[
        { value: LabelExportFormat.Pdf, label: "Download PDF" },
        {
          value: LabelExportFormat.Png,
          label: "Download PNG",
          disabled: pngDisabledReason !== undefined,
          disabledReason: pngDisabledReason,
        },
      ]}
    />,
    <Button key="print" variant="contained" onClick={handlePrint} disabled={exporting}>
      Print
    </Button>,
  ];
}

/**
 * The loaded dialog: format controls beside the live preview, with the two
 * things a reader came to do — print, or save a file — in the action bar.
 *
 * Those two live in the bar rather than beside the selects they belong to
 * because the controls column runs longer than the dialog is tall; inline, the
 * Print button on a print dialog sat below the fold.
 */
function LoadedLabelDialog({
  container,
  open,
  onClose,
}: {
  container: ContainerLabelData;
  open: boolean;
  onClose: () => void;
}): ReactElement {
  const [layout, setLayout] = useState<LabelFormat>(
    container.facility?.defaultLabelFormat ?? LabelFormat.Full
  );
  const [fileType, setFileType] = useState<LabelExportFormat>(LabelExportFormat.Pdf);
  const [size, setSize] = useState<LabelSizeOption>("4x6");
  // No UI sets this anymore -- see the "Not showing the Encamp-logo switch"
  // comment in PrintFormatCard.tsx -- but it stays real state (not a literal
  // `true`) so re-enabling the switch is just adding back a control wired to
  // it.
  const [branded] = useState(true);
  const [note, setNote] = useState(container.labelNote ?? "");

  // The QR-only layout prints just the branded code, so the note has nowhere
  // to go on it.
  const noteApplies = layout !== LabelFormat.QrOnly;

  const pngDisabledReason = pngDisabledReasonFor(container, layout);

  const { exporting, handleNoteBlur, handleExport, handlePrint } = useLabelActions(container, {
    note,
    layout,
    branded,
    size,
  });

  return (
    <LabelDialogShell
      open={open}
      onClose={onClose}
      actions={labelDialogActions({
        // A PNG picked on a layout that can carry it falls back to PDF when
        // the layout changes to one that cannot.
        fileType: pngDisabledReason ? LabelExportFormat.Pdf : fileType,
        setFileType,
        handleExport,
        handlePrint,
        exporting,
        pngDisabledReason,
      })}
    >
      <Grid container spacing={4} alignItems="flex-start" sx={{ pt: 1 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Eyebrow mt={0}>Label format</Eyebrow>
          <FormatRadioGroup layout={layout} onChange={setLayout} />
          {/* Beside the format rather than on the download button: the
              preview redraws at this size, so the choice belongs where a
              reader can see what it did. */}
          <Box sx={{ mt: 3 }}>
            <Eyebrow mt={0}>Page size</Eyebrow>
            <PageSizeToggle value={size} onChange={setSize} />
          </Box>
          {/* Hidden rather than disabled for QR-code-only, which prints no
              note at all. The value stays in state, so toggling back to Full
              label brings back whatever was typed. */}
          {noteApplies && (
            <Box sx={{ mt: 3 }}>
              <Eyebrow mt={0}>Label note</Eyebrow>
              <TextField
                multiline
                minRows={3}
                fullWidth
                // The visible label is the Eyebrow above, so name the control
                // itself here -- on TextField, `aria-label` would land on the
                // wrapper rather than the textarea.
                slotProps={{ htmlInput: { "aria-label": "Label note" } }}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                onBlur={handleNoteBlur}
                helperText="Prints on the label only"
                sx={{ mt: 1 }}
              />
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <PreviewCard
            container={container}
            layout={layout}
            branded={branded}
            note={note}
            size={size}
          />
        </Grid>
      </Grid>
    </LabelDialogShell>
  );
}

/**
 * The dialog chrome both states share, so the title, width and Done button
 * can't drift between the loaded dialog and the one still fetching.
 */
function LabelDialogShell({
  open,
  onClose,
  actions = [],
  children,
}: {
  open: boolean;
  onClose: () => void;
  actions?: ReactElement[];
  children: ReactElement;
}): ReactElement {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      // Without this the dialog sizes to its content, so the skeleton opens
      // narrow and snaps to full width the moment the container lands.
      fullWidth
      title="Print or download label"
      // Nothing here is staged: Print and Download act immediately and the
      // note saves on blur, so Save/Cancel would imply pending changes that
      // don't exist -- and with the two real actions in the bar, a Done button
      // next to them just competes. The corner X dismisses.
      hideCancelButton
      noPrimaryAction
      additionalPrimaryActionElements={actions}
    >
      {children}
    </Dialog>
  );
}

/** The dialog's shape before its data arrives -- the same two-pane split at
 * roughly the same heights the loaded body settles at, so opening it doesn't
 * jump. The identity line is a skeleton too: the real one is a subheader, and
 * without a stand-in everything below it shifts up a row. */
function ContainerLabelDialogSkeleton(): ReactElement {
  return (
    <Grid container spacing={4} alignItems="flex-start" sx={{ pt: 1 }}>
      <Grid size={{ xs: 12, md: 5 }}>
        {/* Label format: an eyebrow over the two option cards. */}
        <Skeleton variant="text" width={96} height={20} />
        <Skeleton variant="rectangular" height={86} sx={{ mt: 1, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={86} sx={{ mt: 1, borderRadius: 1 }} />
        {/* Label note. */}
        <Skeleton variant="text" width={80} height={20} sx={{ mt: 3 }} />
        <Skeleton variant="rectangular" height={100} sx={{ mt: 1, borderRadius: 1 }} />
      </Grid>
      <Grid size={{ xs: 12, md: 7 }}>
        {/* The stage is a fixed height whether or not it has a label in it, so
            matching it here is what keeps the dialog from resizing when the
            real artifact arrives. */}
        <Skeleton variant="rectangular" height={PREVIEW_STAGE_HEIGHT} sx={{ borderRadius: 2 }} />
        <Skeleton variant="text" width={110} height={32} sx={{ mt: 1, ml: "auto" }} />
      </Grid>
    </Grid>
  );
}

export type ContainerLabelDialogProps = {
  containerId: string | undefined;
  open: boolean;
  onClose: () => void;
};

/**
 * Everything you can do with a container's label: pick the format, check the
 * preview, add a note, then print or download it.
 *
 * A dialog rather than the page this used to be, because every one of those
 * choices belongs to the container the reader already has open -- sending them
 * to a separate route to make them meant losing the container behind it. It
 * still has a URL, so it can be linked to and survives a refresh.
 */
export function ContainerLabelDialog({
  containerId,
  open,
  onClose,
}: ContainerLabelDialogProps): ReactElement {
  const { data, loading } = useQuery(GET_CONTAINER_LABEL_QUERY, {
    variables: { id: containerId ?? "" },
    skip: !containerId || !open,
  });
  const container = data?.wasteContainer;

  // A loaded container gets its own dialog so the format state and the
  // Print/Download actions live together; the other two states share the same
  // shell with nothing to act on.
  if (container) {
    return <LoadedLabelDialog container={container} open={open} onClose={onClose} />;
  }

  return (
    <LabelDialogShell
      open={open}
      onClose={onClose}
      // Placeholders, not an empty bar: with no actions the shell drops the
      // whole footer, and the dialog grew by its height the moment the
      // container landed.
      actions={
        loading
          ? [
              <Button key="download" variant="outlined" disabled>
                Download PDF
              </Button>,
              <Button key="print" variant="contained" disabled>
                Print 4 × 6
              </Button>,
            ]
          : []
      }
    >
      {loading ? (
        <ContainerLabelDialogSkeleton />
      ) : (
        <Stack spacing={1} sx={{ py: 4 }} alignItems="center">
          <Typography variant="h6">Container not found</Typography>
          <Typography variant="body2" color="text.secondary">
            The container may have been archived, or the link may be incorrect.
          </Typography>
        </Stack>
      )}
    </LabelDialogShell>
  );
}
